import { Check, Minus, X } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';

type Cell = 'yes' | 'no' | 'partial';
type Row = { label: string; feeling: Cell; tableur: Cell; velko: Cell };

const ROWS: ReadonlyArray<Row> = [
  { label: 'Coût réel de chaque boisson (pour cost)', feeling: 'no', tableur: 'partial', velko: 'yes' },
  { label: 'Consommation déduite à chaque vente', feeling: 'no', tableur: 'no', velko: 'yes' },
  { label: 'Écart théorique vs réel chiffré en F CFA', feeling: 'no', tableur: 'partial', velko: 'yes' },
  { label: 'Détection du surdosage et du coulage', feeling: 'no', tableur: 'no', velko: 'yes' },
  { label: 'Comptage à l’aveugle (anti-triche)', feeling: 'no', tableur: 'no', velko: 'yes' },
  { label: 'Fonctionne hors ligne au comptoir', feeling: 'partial', tableur: 'no', velko: 'yes' },
  { label: 'Sans ressaisie ni formule à maintenir', feeling: 'partial', tableur: 'no', velko: 'yes' },
];

const COLS = [
  { key: 'feeling' as const, label: 'Au feeling', sub: 'Le pifomètre' },
  { key: 'tableur' as const, label: 'Tableur Excel', sub: 'Saisie manuelle' },
  { key: 'velko' as const, label: 'Velko Bar', sub: 'Automatique', highlight: true },
];

function CellIcon({ state }: { state: Cell }) {
  if (state === 'yes') {
    return (
      <span className="flex h-7 w-7 items-center justify-center border-2 border-charcoal-800 bg-lime-500 text-charcoal-800">
        <Check className="h-4 w-4" strokeWidth={3} aria-label="Oui" />
      </span>
    );
  }
  if (state === 'partial') {
    return (
      <span className="flex h-7 w-7 items-center justify-center border-2 border-charcoal-300 bg-white text-charcoal-400">
        <Minus className="h-4 w-4" strokeWidth={3} aria-label="Partiel" />
      </span>
    );
  }
  return (
    <span className="flex h-7 w-7 items-center justify-center border-2 border-charcoal-300 bg-white text-terracotta-400">
      <X className="h-4 w-4" strokeWidth={3} aria-label="Non" />
    </span>
  );
}

export default function BarComparisonV2() {
  return (
    <section className="bg-paper py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <SectionLabel>Pourquoi changer</SectionLabel>
        <h2 className="mt-3 max-w-3xl font-marketing-display text-[clamp(30px,4.4vw,56px)] font-bold leading-[1.02] tracking-[-0.035em] text-charcoal-800">
          Le feeling et le tableur <span className="underline-lime">te coûtent cher.</span>
        </h2>
        <p className="mt-4 max-w-xl font-marketing-body text-[17px] leading-[1.55] text-charcoal-600">
          Compter au pifomètre ou recopier dans Excel laisse fuir la marge sans que tu le voies.
          Voici ce que tu gagnes en passant au contrôle automatique.
        </p>

        <div className="mt-12 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse border-2 border-charcoal-800 bg-white shadow-neobrutal-8">
            <thead>
              <tr>
                <th className="border-b-2 border-charcoal-800 bg-white px-5 py-5 text-left align-bottom font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-500">
                  Ce que tu suis
                </th>
                {COLS.map((col) => (
                  <th
                    key={col.key}
                    className={`border-b-2 border-l-2 border-charcoal-800 px-5 py-5 text-center ${col.highlight ? 'bg-lime-500' : 'bg-white'}`}
                  >
                    <div className="font-marketing-display text-[16px] font-bold leading-tight text-charcoal-800">
                      {col.label}
                    </div>
                    <div className="mt-0.5 font-marketing-mono text-[10px] uppercase tracking-[0.08em] text-charcoal-600">
                      {col.sub}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.label} className="border-b-2 border-charcoal-200 last:border-b-0">
                  <td className="px-5 py-4 font-marketing-body text-[14.5px] font-medium text-charcoal-700">
                    {row.label}
                  </td>
                  {COLS.map((col) => (
                    <td
                      key={col.key}
                      className={`border-l-2 border-charcoal-200 px-5 py-4 ${col.highlight ? 'bg-lime-50' : ''}`}
                    >
                      <div className="flex justify-center">
                        <CellIcon state={row[col.key]} />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
